import { createSystemVariables, setVariableMention } from "./variablesUtilities";
import { VariablesState } from "../../../models/states/VariablesState";
import { DeviceModel } from "../../../models/DeviceModel";
import { RespondentModel } from "../../../models/RespondentModel";
import { PageModel } from "../../../models/PageModel";
import { MentionModel } from "../../../models/MentionModel";
import { getSettingBoolean } from "../../../models/utilities/settingUtilities";
import { NavigationState } from "../../../models/states/NavigationState";
import { QuestionnaireModel } from "../../../models/QuestionnaireModel";
import {
    getVariableRange,
    getBlockWithQuestion,
    getBlockWithVariable,
} from "../../../models/utilities/questionnaireUtilities";
import { SystemVariableModel } from "../../../models/SystemVariableModel";
import {
    NAVIGATION_BRANCH_TO,
    QUESTIONNAIRE_CHANGED,
    DEVICE_CHANGED,
    RESPONDENT_CHANGED,
    RESPONDENT_SET_EMAIL,
    RESPONDENT_SET_PHONE,
    RESPONDENT_SET_TIMEZONE,
    CHANGE_LANGUAGE,
    SET_VARIABLE_MENTIONS,
    NAVIGATION_NEXT_PAGE,
    NAVIGATION_PREVIOUS_PAGE,
    SET_LOOP_ROW,
    NAVIGATION_RESET,
    RESET_MENTIONS,
    SET_SERVER_TRACKED_ANSWERS,
    INVALIDATE_CLIENT_VARIABLES,
    LOAD_ANSWERS_DTO,
    SET_MULTIPLE_VARIABLES_MENTIONS,
    RESPONDENT_SET_DISPOSITIONCODE,
    RESPONDENT_SET_RESPONSE_QUALITY,
} from "../../actions";
import { ActionTypes } from "../../actions/types";
import { BranchToOptions } from "../../../models/BranchToOptions";
import { each, difference, reduce } from "../../../utils/lodash";
import { BlockModel } from "../../../models/BlockModel";
import { AnswerModel } from "../../../models/AnswerModel";
import { MatrixModel } from "../../../models/MatrixModel";
import { MentionBuilderDelegate } from "../../../models/builders/MentionBuilder";
import { probedQuestionsSliceReducer } from "./probedQuestionsSliceReducer";
import { probingStatusSliceReducer } from "./probingStatusSliceReducer";

const surveyChanged = (
    state: VariablesState,
    questionnaire: QuestionnaireModel,
    currentPage: PageModel,
    respondent: RespondentModel,
) => ({
    ...state,
    system: {
        ...state.system,
        ...createSystemVariables(respondent, currentPage),
        $PNAME: new SystemVariableModel(questionnaire.name),
        $A: new SystemVariableModel((respondent.accessCount || 1).toString()),
        $X: new SystemVariableModel(questionnaire.surveyId.toString()),
        $F: new SystemVariableModel(questionnaire.surveyId.toString()),
    },
    mentionsByVariableName: {},
    currentLoopByBlockName: {},
});

const deviceChanged = (state: VariablesState, device: DeviceModel) => ({
    ...state,
    system: {
        ...state.system,
        $DEVICE: new SystemVariableModel(device?.type),
        $DEVICEOS: new SystemVariableModel(device?.os),
        $DEVICEOSVERSION: new SystemVariableModel(device?.version),
        $DEVICEBROWSER: new SystemVariableModel(device?.browser),
        $DEVICEBROWSERVERSION: new SystemVariableModel(device?.version),
    },
});

const respondentChanged = (
    state: VariablesState,
    currentPage: PageModel,
    respondent: RespondentModel,
) => ({
    ...state,
    system: {
        ...state.system,
        ...createSystemVariables(respondent, currentPage),
        $Q: new SystemVariableModel(String(respondent.respondentId || 0)),
        $PIN: new SystemVariableModel(respondent.pin),
        $EMAIL: new SystemVariableModel(respondent.email !== undefined ? respondent.email : ""),
        $PHONE: new SystemVariableModel(respondent.phone !== undefined ? respondent.phone : ""),
        $L: new SystemVariableModel(respondent.language),
        $OFFLINEUSER: new SystemVariableModel(
            respondent.offlineUserName,
            respondent.offlineUserId.toString(),
        ),
        $PANELISTID: new SystemVariableModel(String(respondent.panelistId)),
        $SAMPLEID: new SystemVariableModel(String(respondent.sampleId)),
        $PANELISTPOINTS: new SystemVariableModel(String(respondent.panelistPoints)),
    },
});

const respondentLanguageChanged = (state: VariablesState, respondent: RespondentModel) => ({
    ...state,
    system: {
        ...state.system,
        $L: new SystemVariableModel(respondent.language),
    },
});

const autoClean = (
    state: VariablesState,
    previousPage: PageModel,
    questionnaire: QuestionnaireModel,
    branchTo: BranchToOptions,
) => {
    if (!branchTo.autoClean) return state;
    const includeFirst =
        branchTo.isPreload && getSettingBoolean(questionnaire, "CLEAN_QUESTION_PRELOAD");
    const mentionsToClean = getMentionsToClean(
        state,
        questionnaire,
        branchTo.origin,
        branchTo.destination,
        previousPage,
        includeFirst,
    );

    each(mentionsToClean, (mentions, variableName) => {
        state = {
            ...state,
            mentionsByVariableName: {
                ...state.mentionsByVariableName,
                [variableName]: difference(state.mentionsByVariableName[variableName], mentions),
            },
        };
    });
    return state;
};

function getMentionsToClean(
    state: VariablesState,
    questionnaire: QuestionnaireModel,
    startQuestion: string,
    destinationQuestion: string,
    previousPage: PageModel,
    includeFirst: boolean,
) {
    const variableRange = getVariableRange(
        questionnaire,
        startQuestion,
        destinationQuestion,
        includeFirst,
    );

    const startBlock = getBlockWithQuestion(questionnaire, startQuestion);
    const endBlock = getBlockWithQuestion(questionnaire, destinationQuestion);

    const isMentionOnRange = (
        mention: MentionModel,
        variableName: string,
        variableBlock: BlockModel,
    ) => {
        if (variableRange.map((v) => v.name).includes(variableName)) {
            if (variableBlock.index === startBlock.index) {
                if (mention.matrix >= previousPage.loopIndex) {
                    return true;
                }
            } else if (endBlock.index > startBlock.index) {
                return true;
            }
        }

        // If we are branching to another block, be sure to add to the range variables from higher loops of this block
        return (
            endBlock?.index > startBlock.index &&
            variableBlock.index === startBlock.index &&
            mention.matrix > previousPage.loopIndex
        );
    };

    return reduce(
        state.mentionsByVariableName,
        (result, mentions, variableName) => {
            const variableBlock = getBlockWithVariable(questionnaire, variableName);
            return {
                ...result,
                [variableName]: mentions.filter((m) =>
                    isMentionOnRange(m, variableName, variableBlock),
                ),
            };
        },
        {},
    );
}

const pageChanged = (
    state: VariablesState,
    action: ActionTypes,
    previousPage: PageModel,
    navigation: NavigationState,
    questionnaire: QuestionnaireModel,
    respondent: RespondentModel,
) => {
    let newState = { ...state };
    // If the page has been branched, apply auto cleaning.
    if (
        action.type === NAVIGATION_BRANCH_TO &&
        getSettingBoolean(questionnaire, "AUTO_DATA_CLEANING")
    )
        newState = autoClean(newState, previousPage, questionnaire, action.options);
    return {
        ...newState,
        system: {
            ...newState.system,
            ...createSystemVariables(respondent, navigation.currentPage),
        },
    };
};

const navigationReset = (
    state: VariablesState,
    clearAnswers: boolean,
    respondent: RespondentModel,
) => {
    if (clearAnswers) state = { ...state, mentionsByVariableName: {} };
    return {
        ...state,
        system: {
            ...state.system,
            ...createSystemVariables(respondent),
        },
        currentLoopByBlockName: {},
    };
};

const loadAnswersDto = (
    state: VariablesState,
    navigation: NavigationState,
    questionnaire: QuestionnaireModel,
    answers: AnswerModel[],
) => {
    return reduce(
        answers,
        (newState, answer) =>
            loadMatrices(newState, navigation, questionnaire, answer.variableName, answer.matrices),
        state,
    );
};

const loadMatrices = (
    state: VariablesState,
    navigation: NavigationState,
    questionnaire: QuestionnaireModel,
    variableName: string,
    matrices: MatrixModel[],
): VariablesState => {
    return reduce(
        matrices,
        (newState, matrix) =>
            setVariableMention(
                newState,
                navigation,
                questionnaire,
                variableName,
                matrix.index,
                (mentions) => {
                    mentions.clear();
                    each(matrix.mentions, (mention) =>
                        mentions.add(
                            mention.value,
                            mention.openEnd,
                            mention.customChoiceIndex,
                            mention.pastedContent,
                        ),
                    );
                    return mentions;
                },
            ),
        state,
    );
};

const setMultipleVariablesMentions = (
    state: VariablesState,
    navigation: NavigationState,
    questionnaire: QuestionnaireModel,
    mentionsByVariable: { [variableName: string]: MentionBuilderDelegate },
) => {
    return reduce(
        mentionsByVariable,
        (newState, builder, variableName) =>
            setVariableMention(
                newState,
                navigation,
                questionnaire,
                variableName.split(":")[0],
                undefined,
                builder,
            ),
        state,
    );
};

export const variablesSliceReducer = (
    state: VariablesState,
    action: ActionTypes,
    previousPage: PageModel,
    questionnaire: QuestionnaireModel,
    respondent: RespondentModel,
    navigation: NavigationState,
): any => {
    // Handle the probedQuestions slice separately
    const probedQuestions = probedQuestionsSliceReducer(state.probedQuestions, action);
    
    // Handle the probingStatus slice separately
    const probingStatus = probingStatusSliceReducer(state.probingStatus, action);
    
    // If neither slice changed, no need to create a new state object
    if (probedQuestions === state.probedQuestions && probingStatus === state.probingStatus) {
        // Handle all other state changes as before
        switch (action.type) {
            case QUESTIONNAIRE_CHANGED:
                return surveyChanged(state, action.questionnaire, navigation.currentPage, respondent);
            case DEVICE_CHANGED:
                return deviceChanged(state, action.device);
            case RESPONDENT_CHANGED:
            case RESPONDENT_SET_EMAIL:
            case RESPONDENT_SET_PHONE:
            case RESPONDENT_SET_TIMEZONE:
            case RESPONDENT_SET_DISPOSITIONCODE:
            case RESPONDENT_SET_RESPONSE_QUALITY:
                return respondentChanged(state, navigation.currentPage, respondent);
            case CHANGE_LANGUAGE:
                return respondentLanguageChanged(state, respondent);
            case SET_VARIABLE_MENTIONS:
                return setVariableMention(
                    state,
                    navigation,
                    questionnaire,
                    action.variableName,
                    action.matrixIndex,
                    action.builder,
                );
            case SET_MULTIPLE_VARIABLES_MENTIONS:
                return setMultipleVariablesMentions(
                    state,
                    navigation,
                    questionnaire,
                    action.mentionsByVariable,
                );
            case NAVIGATION_NEXT_PAGE:
            case NAVIGATION_PREVIOUS_PAGE:
            case NAVIGATION_BRANCH_TO:
            case SET_LOOP_ROW:
                return pageChanged(state, action, previousPage, navigation, questionnaire, respondent);
            case NAVIGATION_RESET:
                return navigationReset(state, action.clearAnswers, respondent);
            case RESET_MENTIONS:
                return {
                    ...state,
                    mentionsByVariableName: action.mentions,
                };
            case SET_SERVER_TRACKED_ANSWERS:
                return {
                    ...state,
                    serverSideTrackedAnswers: action.answers,
                };
            case INVALIDATE_CLIENT_VARIABLES:
                return {
                    ...state,
                    invalidateClientSideVariables: true,
                };
            case LOAD_ANSWERS_DTO:
                return loadAnswersDto(state, navigation, questionnaire, action.answers);
            default:
                return state;
        }
    } else {
        // Return a new state with updated probed questions and status
        return {
            ...state,
            probedQuestions,
            probingStatus,
        };
    }
};
